import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { X, Play, Pause, SkipBack, SkipForward } from "lucide-react";

const AudioPlayerToast = ({ isOpen, onClose, audioSrc = "/podcast.mp3" }) => {
  const toastRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  // Audio neu laden wenn Toast geöffnet wird
  useEffect(() => {
    if (isOpen) {
      const audio = new Audio(audioSrc);
      audio.preload = "metadata";
      
      const handleLoaded = () => {
        setIsLoaded(true);
        setDuration(audio.duration);
        audioRef.current = audio;
      };

      audio.addEventListener("loadedmetadata", handleLoaded);
      
      return () => {
        audio.removeEventListener("loadedmetadata", handleLoaded);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        setIsLoaded(false);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      };
    }
  }, [isOpen, audioSrc]);

  // GSAP Animation für Toast
  useEffect(() => {
    const toast = toastRef.current;
    if (!toast) return;

    if (isOpen) {
      // Toast einblenden von unten
      gsap.set(toast, {
        y: 100,
        opacity: 0,
        scale: 0.95,
      });
      
      gsap.to(toast, {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.4,
        ease: "power2.out",
      });
    } else {
      // Toast ausblenden nach unten
      gsap.to(toast, {
        y: 100,
        opacity: 0,
        scale: 0.95,
        duration: 0.3,
        ease: "power2.in",
      });
    }
  }, [isOpen]);

  // Audio Event Handlers für Zeit-Updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    const updateTime = () => setCurrentTime(audio.currentTime || 0);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [isLoaded]);

  // Play/Pause Status Management
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    if (!isPlaying) {
      audio.pause();
    }
  }, [isPlaying, isLoaded]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        await audio.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Playback failed:", error);
      setIsPlaying(false);
    }
  };

  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;
    
    const newTime = Math.max(0, audio.currentTime - 10);
    audio.currentTime = newTime;
  };

  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;
    
    const newTime = Math.min(audio.duration, audio.currentTime + 10);
    audio.currentTime = newTime;
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop - nur für Click-to-Close, nicht für Scroll-Blocking */}
      
      {/* Toast Container */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 pointer-events-auto">
        <div
          ref={toastRef}
          className="bg-white/95 backdrop-blur-md border border-white/60 rounded-2xl shadow-2xl p-4 w-80 max-w-[90vw]"
          style={{
            boxShadow: "0 20px 60px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.2)"
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-neutral-900 truncate">
                Tom Oppenhäuser Podcast
              </h3>
              <p className="text-xs text-neutral-600 truncate">
                Coaching & Transformation
              </p>
            </div>
            
            <button
              onClick={handleClose}
              className="flex-none w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors grid place-items-center"
              aria-label="Player schließen"
            >
              <X size={16} className="text-neutral-600" />
            </button>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mb-3">
            {/* Skip Backward 10s */}
            <button
              onClick={skipBackward}
              className="flex-none w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors grid place-items-center"
              aria-label="10 Sekunden zurück"
            >
              <SkipBack size={20} className="text-neutral-700" />
            </button>

            {/* Play/Pause Button */}
            <button
              onClick={togglePlayPause}
              className="flex-none w-12 h-12 rounded-full bg-[#F59E0B] hover:bg-[#D97706] transition-colors grid place-items-center shadow-md"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause size={20} className="text-white" />
              ) : (
                <Play size={20} className="text-white ml-0.5" />
              )}
            </button>

            {/* Skip Forward 10s */}
            <button
              onClick={skipForward}
              className="flex-none w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors grid place-items-center"
              aria-label="10 Sekunden vor"
            >
              <SkipForward size={20} className="text-neutral-700" />
            </button>
          </div>

          {/* Simple Time Display */}
          {isLoaded && (
            <div className="text-center text-sm text-neutral-600 mb-2">
              <span>{formatTime(currentTime)}</span>
              <span className="mx-2">/</span>
              <span>{formatTime(duration)}</span>
            </div>
          )}

          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            src={audioSrc}
            preload="metadata"
            crossOrigin="anonymous"
          />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayerToast;
